import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { buscarConferencia, atualizarConferencia } from '../../../services/conferencia';
import { apiService } from '../../../services/api';
import { listarClientes } from '../../../services/clientes';
import type { TipoConferencia, StatusConferencia } from '../../../types/conferencia';
import '../Conferencia.css';

interface Motorista {
  id: number;
  nome: string;
}

interface ClienteOption {
  id: number;
  razao_social: string;
}

const ConferenciaEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [motoristas, setMotoristas] = useState<Motorista[]>([]);
  const [clientes, setClientes] = useState<ClienteOption[]>([]);
  const [formData, setFormData] = useState({
    tipo: 'carregamento' as TipoConferencia,
    status: 'pendente' as StatusConferencia,
    placa_veiculo: '',
    motorista_id: '' as string | number,
    cliente_id: '' as string | number,
    transportadora: '',
    manifesto: '',
    rota: '',
    regiao_entrega: '',
    observacoes: '',
    total_itens_esperado: 0
  });

  useEffect(() => {
    apiService.getDrivers({ page: 1, limit: 200 })
      .then(res => {
        if (res.success && res.data) {
          setMotoristas(res.data.map((d: any) => ({ id: d.id, nome: d.nome })));
        }
      })
      .catch(() => {});

    listarClientes({ limit: 200, ativo: 'true' })
      .then(res => {
        if (res.success && res.clientes) {
          setClientes(res.clientes.map(c => ({ id: c.id, razao_social: c.razao_social })));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const carregar = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const result = await buscarConferencia(parseInt(id));
        if (result.success) {
          const c = result.conferencia;
          setFormData({
            tipo: c.tipo as TipoConferencia,
            status: c.status as StatusConferencia,
            placa_veiculo: c.placa_veiculo || '',
            motorista_id: c.motorista_id || '',
            cliente_id: c.cliente_id || '',
            transportadora: c.transportadora || '',
            manifesto: c.manifesto || '',
            rota: c.rota || '',
            regiao_entrega: c.regiao_entrega || '',
            observacoes: c.observacoes || '',
            total_itens_esperado: c.total_itens_esperado || 0
          });
        }
      } catch (err) {
        setError('Erro ao carregar conferência');
      } finally {
        setLoading(false);
      }
    };
    carregar();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setError('');
    setSaving(true);

    try {
      const payload = {
        ...formData,
        motorista_id: formData.motorista_id ? Number(formData.motorista_id) : undefined,
        cliente_id: formData.cliente_id ? Number(formData.cliente_id) : undefined
      };
      const result = await atualizarConferencia(parseInt(id), payload);
      if (result.success) {
        navigate(`/conferencia/${id}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao atualizar conferência');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <button className="conferencia-back-link" onClick={() => navigate(`/conferencia/${id}`)}>
        <i className="fas fa-arrow-left"></i> Voltar
      </button>

      <div className="card">
        <div className="card-header">
          <h2><i className="fas fa-box-open" style={{ marginRight: 8 }}></i> Editar Conferência #{id}</h2>
        </div>
        <div className="card-body">
          {error && <div className="conferencia-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="conferencia-form-grid">
              <div className="form-group">
                <label>Tipo de Conferência *</label>
                <select className="form-control" value={formData.tipo} onChange={(e) => setFormData(prev => ({ ...prev, tipo: e.target.value as TipoConferencia }))} required>
                  <option value="pre_carregamento">Pré-Carregamento</option>
                  <option value="carregamento">Carregamento</option>
                  <option value="descarregamento">Descarregamento</option>
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select className="form-control" value={formData.status} onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as StatusConferencia }))}>
                  <option value="pendente">Pendente</option>
                  <option value="em_andamento">Em Andamento</option>
                  <option value="parcial">Parcial</option>
                  <option value="concluida">Concluída</option>
                  <option value="divergente">Divergente</option>
                  <option value="cancelada">Cancelada</option>
                </select>
              </div>
              <div className="form-group">
                <label>Placa do Veículo</label>
                <input type="text" className="form-control" value={formData.placa_veiculo} onChange={(e) => setFormData(prev => ({ ...prev, placa_veiculo: e.target.value.toUpperCase() }))} placeholder="ABC-1234" maxLength={10} />
              </div>
              <div className="form-group">
                <label>Motorista</label>
                <select className="form-control" value={formData.motorista_id} onChange={(e) => setFormData(prev => ({ ...prev, motorista_id: e.target.value }))}>
                  <option value="">Selecione um motorista</option>
                  {motoristas.map(m => (<option key={m.id} value={m.id}>{m.nome}</option>))}
                </select>
              </div>
              <div className="form-group">
                <label>Cliente</label>
                <select className="form-control" value={formData.cliente_id} onChange={(e) => setFormData(prev => ({ ...prev, cliente_id: e.target.value }))}>
                  <option value="">Selecione um cliente</option>
                  {clientes.map(c => (<option key={c.id} value={c.id}>{c.razao_social}</option>))}
                </select>
              </div>
              <div className="form-group">
                <label>Transportadora</label>
                <input type="text" className="form-control" value={formData.transportadora} onChange={(e) => setFormData(prev => ({ ...prev, transportadora: e.target.value }))} placeholder="Nome da transportadora" />
              </div>
              <div className="form-group">
                <label>Manifesto</label>
                <input type="text" className="form-control" value={formData.manifesto} onChange={(e) => setFormData(prev => ({ ...prev, manifesto: e.target.value }))} placeholder="Número do manifesto" />
              </div>
              <div className="form-group">
                <label>Rota</label>
                <input type="text" className="form-control" value={formData.rota} onChange={(e) => setFormData(prev => ({ ...prev, rota: e.target.value }))} placeholder="Identificação da rota" />
              </div>
              <div className="form-group">
                <label>Região de Entrega</label>
                <input type="text" className="form-control" value={formData.regiao_entrega} onChange={(e) => setFormData(prev => ({ ...prev, regiao_entrega: e.target.value }))} placeholder="Região/zona de entrega" />
              </div>
              <div className="form-group">
                <label>Total de Itens Esperado</label>
                <input type="number" className="form-control" min="0" value={formData.total_itens_esperado} onChange={(e) => setFormData(prev => ({ ...prev, total_itens_esperado: parseInt(e.target.value) || 0 }))} />
              </div>
            </div>

            <div className="form-group" style={{ marginTop: 16 }}>
              <label>Observações</label>
              <textarea className="form-control" value={formData.observacoes} onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))} rows={3} placeholder="Observações sobre a conferência..." style={{ resize: 'none' }} />
            </div>

            <div className="conferencia-form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => navigate(`/conferencia/${id}`)}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Salvando...' : 'Salvar Alterações'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConferenciaEdit;
